<script setup>
import { computed, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import inboxesAPI from 'dashboard/api/inboxes';
import { createContactSearcher } from 'dashboard/components-next/NewConversation/helpers/composeConversationHelper';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TagInput from 'dashboard/components-next/taginput/TagInput.vue';

const props = defineProps({
  inboxId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const dialogRef = ref(null);
const isCreating = ref(false);

const form = reactive({ subject: '', description: '' });
const participants = ref([]);
const contactResults = ref([]);
const isSearchingContacts = ref(false);
const showContactsDropdown = ref(false);

const searchContacts = createContactSearcher();

const isFormInvalid = computed(
  () => !form.subject.trim() || !participants.value.length
);

// Existing contacts are picked straight from the dropdown (already valid numbers); anything
// typed by hand still has to pass TagInput's own libphonenumber-js validation (type="tel"),
// so a member can only be added with a well-formed, dial-able number either way.
const contactMenuItems = computed(() =>
  contactResults.value
    .filter(contact => contact.phoneNumber)
    .map(contact => ({
      value: contact.phoneNumber,
      label: `${contact.name} (${contact.phoneNumber})`,
      phoneNumber: contact.phoneNumber,
      action: 'contact',
    }))
);

const onParticipantSearch = debounce(
  async query => {
    if (!query) {
      contactResults.value = [];
      showContactsDropdown.value = false;
      return;
    }
    showContactsDropdown.value = true;
    isSearchingContacts.value = true;
    try {
      const results = await searchContacts(query, { reachableOnly: false });
      contactResults.value = results || [];
    } finally {
      isSearchingContacts.value = false;
    }
  },
  400,
  false
);

const resetForm = () => {
  form.subject = '';
  form.description = '';
  participants.value = [];
  contactResults.value = [];
  showContactsDropdown.value = false;
};

const open = () => {
  resetForm();
  dialogRef.value?.open();
};

const closeDialog = () => {
  dialogRef.value?.close();
};

const handleConfirm = async () => {
  if (isFormInvalid.value || isCreating.value) return;
  isCreating.value = true;
  try {
    await inboxesAPI.createEvolutionGroup(props.inboxId, {
      subject: form.subject.trim(),
      description: form.description.trim() || undefined,
      participants: participants.value,
    });
    useAlert(t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.CREATE_SUCCESS'));
    closeDialog();
  } catch (error) {
    useAlert(t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.CREATE_ERROR'));
  } finally {
    isCreating.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="lg"
    overflow-y-auto
    @confirm="handleConfirm"
    @close="resetForm"
  >
    <div class="flex flex-col gap-4">
      <span class="py-1 text-sm font-medium text-n-slate-12">
        {{ t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.CREATE_DIALOG_TITLE') }}
      </span>
      <Input
        v-model="form.subject"
        :label="t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.SUBJECT_LABEL')"
        :disabled="isCreating"
        autofocus
      />
      <Input
        v-model="form.description"
        :label="t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.DESCRIPTION_LABEL')"
        :disabled="isCreating"
      />
      <div class="flex flex-col gap-1.5">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.PARTICIPANTS_LABEL') }}
        </label>
        <div
          class="px-2 py-2 rounded-xl outline outline-1 -outline-offset-1 outline-n-weak hover:outline-n-strong"
        >
          <TagInput
            v-model="participants"
            type="tel"
            allow-create
            :menu-items="contactMenuItems"
            :show-dropdown="showContactsDropdown"
            :is-loading="isSearchingContacts"
            :disabled="isCreating"
            :placeholder="
              t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.PARTICIPANTS_PLACEHOLDER')
            "
            @input="onParticipantSearch"
            @on-click-outside="showContactsDropdown = false"
          />
        </div>
        <p class="text-xs text-n-slate-11">
          {{ t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.PARTICIPANTS_HELP_TEXT') }}
        </p>
      </div>
    </div>

    <template #footer>
      <div class="flex items-center justify-between w-full gap-3">
        <Button
          :label="t('DIALOG.BUTTONS.CANCEL')"
          variant="link"
          type="reset"
          class="h-10 hover:!no-underline hover:text-n-brand"
          @click="closeDialog"
        />
        <Button
          :label="t('INBOX_MGMT.EVOLUTION_PROFILE.GROUPS.CREATE_BUTTON')"
          color="blue"
          type="submit"
          :disabled="isFormInvalid || isCreating"
          :is-loading="isCreating"
        />
      </div>
    </template>
  </Dialog>
</template>
